import { lazy } from 'react';

/**
 * Vite can reject a lazy() import once if the module was missing/mid-write during HMR.
 * React then caches that rejected promise — remounting never retries.
 * This wrapper retries once after a short delay, then forces a full reload.
 */
function isImportFailure(err) {
  const message = String(err?.message || err || '');
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module') ||
    /Loading chunk [\w-]+ failed/i.test(message)
  );
}

export function lazyRetry(factory, retries = 3) {
  return lazy(async () => {
    let lastErr;
    for (let attempt = 0; attempt <= retries; attempt += 1) {
      try {
        const mod = await factory();
        try {
          sessionStorage.removeItem('vite-lazy-reload');
        } catch {
          /* private mode */
        }
        return mod;
      } catch (err) {
        lastErr = err;
        if (!isImportFailure(err) || attempt === retries) break;
        // nginx limit_req refills slowly; don't retry in the same burst window
        await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
      }
    }

    try {
      if (typeof window !== 'undefined' && !sessionStorage.getItem('vite-lazy-reload')) {
        sessionStorage.setItem('vite-lazy-reload', '1');
        window.location.reload();
        return new Promise(() => {});
      }
      sessionStorage.removeItem('vite-lazy-reload');
    } catch {
      /* private mode */
    }
    throw lastErr;
  });
}
